import React, { createContext, useContext, useMemo, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import env from '@/utils/env';
import { useAuth } from './AuthContext';
import { useNotifications } from './NotificationContext';

// LocalStorage keys for player state persistence
const KEY_PREFS = 'seniquiz_prefs';
const KEY_PROFILE = 'seniquiz_player_profile';
const KEY_PARTICIPANT_MAP = 'seniquiz_participant_map';
const KEY_LAST_CODE = 'seniquiz_last_code';

/**
 * Generate UUID for participant ID
 */
function uuid() {
  return crypto.randomUUID
    ? crypto.randomUUID()
    : `p_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

const RoomContext = createContext(null);

export function RoomProvider({ children }) {
  const { user } = useAuth();
  const { showToast } = useNotifications();
  const socketRef = useRef(null);

  const [connected, setConnected] = useState(false);
  const [role, setRole] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [code, setCode] = useState(null);
  const [status, setStatus] = useState('idle');
  const [players, setPlayers] = useState([]);
  const [playerCount, setPlayerCount] = useState(0);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [me, setMe] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [lastReveal, setLastReveal] = useState(null);
  const [questionResult, setQuestionResult] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [joinError, setJoinError] = useState(null);
  const [participantId, setParticipantId] = useState(null);
  const [playerName, setPlayerName] = useState(null);
  const [selection, setSelection] = useState({
    questionIndex: null,
    selectedAnswerIds: [],
    lastSentAt: null
  });
  const [lastAckStatus, setLastAckStatus] = useState(null);
  const [finalResults, setFinalResults] = useState({ me: null });

  /**
   * Ensure socket is initialized
   * @param {boolean} wantHost - True if host connection (requires auth token)
   */
  const ensureSocket = useCallback(
    async (wantHost = false) => {
      const desiredRole = wantHost ? 'host' : 'player';

      // If socket exists with the correct role, reuse it (even if not yet connected)
      if (socketRef.current) {
        const currentRole = socketRef.current.auth?.role;

        if (currentRole === desiredRole) {
          console.log('Reusing existing socket with role:', currentRole);
          // If already connected, return immediately
          if (socketRef.current.connected) {
            return socketRef.current;
          }
          // Otherwise, wait for connection
          return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              console.error('Socket connection timeout');
              reject(new Error('Socket connection timeout'));
            }, 5000);

            socketRef.current.once('connect', () => {
              console.log('Socket connect event received (reused)');
              clearTimeout(timeout);
              resolve(socketRef.current);
            });

            socketRef.current.once('connect_error', (err) => {
              console.error('Socket connection error:', err);
              clearTimeout(timeout);
              reject(err);
            });
          });
        }

        // Role mismatch - disconnect and create new socket
        console.log('Role mismatch - disconnecting socket to reconnect with new role');
        socketRef.current.disconnect();
        socketRef.current = null;
      }

      // Double-check: if a socket is being created, another call might be in progress
      if (socketRef.current) {
        console.warn('Socket already exists during creation, reusing it');
        return socketRef.current;
      }

      const auth = {
        role: wantHost ? 'host' : 'player'
      };

      if (wantHost && user) {
        try {
          const token = await user.getIdToken();
          if (token) auth.token = token;

          // Add session ID for single-device enforcement
          const sessionId = localStorage.getItem('sessionId');
          if (sessionId) auth.sessionId = sessionId;
        } catch (err) {
          console.warn('Failed to get auth token for socket:', err);
        }
      }

      // Backend serves Socket.IO at path '/ws' and uses namespace '/ws'
      // We need to connect to the namespace explicitly
      const namespaceUrl = `${env.socketUrl}${env.socketNamespace}`;

      console.log('Creating NEW socket with config:', {
        url: namespaceUrl,
        path: env.socketPath,
        auth,
        role: auth.role
      });

      socketRef.current = io(namespaceUrl, {
        path: env.socketPath,
        transports: ['websocket', 'polling'],
        auth,
        autoConnect: true,
        reconnection: true,
        reconnectionDelayMax: 5000
      });

      const s = socketRef.current;

      console.log('Socket connecting...', s);

      // Remove all previous listeners to avoid duplicates
      s.removeAllListeners();

      // Connection events
      s.on('connect', () => {
        console.log('Socket connected!');
        setConnected(true);
      });
      s.on('disconnect', () => {
        console.log('Socket disconnected');
        setConnected(false);
      });

      // Room state events
      s.on('room.state', (payload) => {
        console.log('Received room.state at', Date.now(), ':', payload);
        // Handle both 'state' and 'status' fields (backend sends different formats)
        const newStatus = payload.state || payload.status || 'idle';

        // Don't override 'podium' status with 'closed' - let players stay on podium screen
        setStatus((currentStatus) => {
          if (currentStatus === 'podium' && newStatus === 'closed') {
            console.log('Ignoring closed status - staying on podium screen');
            return 'podium';
          }
          return newStatus;
        });

        setPlayers(payload.players || []);
        setPlayerCount(payload.playerCount || 0);
        setAnsweredCount(payload.answeredCount || 0);
      });

      s.on('question.start', (payload) => {
        console.log('Received question.start:', payload);
        // Handle both old and new payload formats
        const questionIndex = payload.questionIndex;
        const questionData = payload.payloadForPlayers || payload.question || payload;

        setCurrentQuestion({
          index: questionIndex,
          ...questionData
        });
        setLastReveal(null);
        setQuestionResult(null);
        // Reset selection for new question
        setSelection({ questionIndex, selectedAnswerIds: [], lastSentAt: null });
        setStatus('question');
      });

      s.on('question.end', ({ questionIndex, reveal }) => {
        setLastReveal(reveal);
        // Lock current selection
        setSelection((prev) => ({ ...prev, locked: true }));
      });

      s.on('leaderboard.update', (lb) => {
        setLeaderboard(lb);
        if (lb.me) setMe(lb.me);
        if (lb.questionResult) setQuestionResult(lb.questionResult);
      });

      s.on('podium', (podiumData) => {
        console.log('Received podium event:', podiumData);
        setStatus('podium');
        // Persist final results for player when podium is reached
        setFinalResults({
          top3: podiumData?.top3 || [],
          me:
            podiumData?.me ||
            me ||
            (leaderboard?.me
              ? { rank: leaderboard.me.rank, score: leaderboard.me.score }
              : { rank: null, score: 0 }),
          totalPlayers: podiumData?.totalPlayers || 0
        });
      });

      s.on('kicked', ({ reason }) => {
        showToast({
          type: 'warning',
          message: reason || 'Vous avez été expulsé de la salle.'
        });
        leave();
      });

      s.on('error', ({ code, message }) => {
        setJoinError({ code, message });
        showToast({
          type: 'error',
          message: message || 'Erreur de connexion à la salle.'
        });
      });

      // Wait for socket to connect before returning
      return new Promise((resolve, reject) => {
        if (s.connected) {
          console.log('Socket already connected');
          resolve(s);
          return;
        }

        const timeout = setTimeout(() => {
          console.error('Socket connection timeout');
          reject(new Error('Socket connection timeout'));
        }, 5000);

        s.once('connect', () => {
          console.log('Socket connect event received');
          clearTimeout(timeout);
          resolve(s);
        });

        s.once('connect_error', (err) => {
          console.error('Socket connection error:', err);
          clearTimeout(timeout);
          reject(err);
        });
      });
    },
    [user, showToast]
  );

  /**
   * Player: Join room by code
   */
  const joinByCode = useCallback(
    async ({ code, name, participantId: providedPid }) => {
      console.log('joinByCode called with:', { code, name, participantId: providedPid });
      const s = await ensureSocket(false);
      console.log('Socket ensured:', s?.connected);
      const upperCode = code.toUpperCase();

      // Try to get stored participantId for this code
      const participantMap = JSON.parse(localStorage.getItem(KEY_PARTICIPANT_MAP) || '{}');
      const storedPid = participantMap[upperCode];
      const pid = providedPid || storedPid || uuid();

      console.log('Joining with participantId:', pid);

      // Persist player info
      const profile = JSON.parse(localStorage.getItem(KEY_PROFILE) || '{}');
      profile.name = name;
      localStorage.setItem(KEY_PROFILE, JSON.stringify(profile));
      localStorage.setItem(KEY_LAST_CODE, JSON.stringify({ code: upperCode }));

      return new Promise((resolve) => {
        setRole('player');
        setCode(upperCode);
        setPlayerName(name);
        setParticipantId(pid);
        setJoinError(null);

        console.log('Emitting room.join event:', { code: upperCode, name, participantId: pid });

        // Add timeout to prevent infinite loading
        const timeout = setTimeout(() => {
          console.error('room.join timeout - no response from server');
          setJoinError({
            code: 'TIMEOUT',
            message: 'Le serveur ne répond pas. Vérifiez votre connexion.'
          });
          showToast({
            type: 'error',
            message: 'Le serveur ne répond pas. Vérifiez votre connexion.'
          });
          resolve(false);
        }, 10000); // 10 second timeout

        s.emit('room.join', { code: upperCode, name, participantId: pid }, (ack) => {
          clearTimeout(timeout);
          console.log('room.join acknowledgment received:', ack);
          if (ack?.error) {
            console.error('Join error:', ack.error);
            setJoinError(ack.error);
            showToast({ type: 'error', message: ack.error.message });
            resolve(false);
          } else {
            console.log('Join successful, sessionId:', ack?.sessionId);
            if (ack?.sessionId) {
              setSessionId(ack.sessionId);
              // Save participantId mapping for reconnection
              participantMap[upperCode] = pid;
              localStorage.setItem(KEY_PARTICIPANT_MAP, JSON.stringify(participantMap));
            }
            setLastAckStatus('joined');
            resolve(true);
          }
        });
      });
    },
    [ensureSocket, showToast]
  );

  /**
   * Player: Submit answer (with throttling and state tracking)
   */
  const submitAnswer = useCallback(
    ({ questionIndex, selectedAnswerIds }) => {
      if (!socketRef.current || role !== 'player' || !sessionId) return;

      const now = new Date().toISOString();
      setSelection({ questionIndex, selectedAnswerIds, lastSentAt: now });

      socketRef.current.emit(
        'answer.submit',
        {
          sessionId,
          questionIndex,
          selectedAnswerIds
        },
        (response) => {
          // Handle acknowledgment from server
          if (response?.ok) {
            setLastAckStatus('answer_acked');
          } else {
            setLastAckStatus('answer_error');
            if (response?.error?.message) {
              showToast(response.error.message, 'error');
            }
          }
        }
      );

      setLastAckStatus('answer_sent');
    },
    [role, sessionId, showToast]
  );

  /**
   * Player: Clear current selection
   */
  const clearSelection = useCallback(() => {
    if (currentQuestion) {
      setSelection({
        questionIndex: currentQuestion.index,
        selectedAnswerIds: [],
        lastSentAt: null
      });
    }
  }, [currentQuestion]);

  /**
   * Leave room (player or host)
   */
  const leave = useCallback(() => {
    setRole(null);
    setSessionId(null);
    setCode(null);
    setStatus('idle');
    setPlayers([]);
    setMe(null);
    setCurrentQuestion(null);
    setLastReveal(null);
    setLeaderboard(null);
    setParticipantId(null);
    setPlayerName(null);
    setSelection({ questionIndex: null, selectedAnswerIds: [], lastSentAt: null });
    setLastAckStatus(null);
    setFinalResults({ me: null });

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setConnected(false);
  }, []);

  /**
   * Host: Join room with role='host' to subscribe to room.state
   */
  const hostJoinRoom = useCallback(
    async (sid, code) => {
      const s = await ensureSocket(true);
      setRole('host');
      setSessionId(sid);
      setCode(code);

      // Emit room.join with role='host' to subscribe to room.state without being counted as player
      s.emit('room.join', { sessionId: sid, code, role: 'host' }, (ack) => {
        if (ack?.error) {
          console.error('Host room join failed', ack.error);
          showToast({ type: 'error', message: ack.error.message });
        } else {
          console.log('Host joined room successfully');
        }
      });
    },
    [ensureSocket, showToast]
  );

  /**
   * Host: Start session
   */
  const hostStart = useCallback(
    async (sid) => {
      const s = await ensureSocket(true);
      setRole('host');
      setSessionId(sid);
      s.emit('host.start', { sessionId: sid }, (ack) => {
        if (ack?.error) {
          console.error('Host start failed:', ack.error);
          showToast({
            type: 'error',
            message: ack.error.message || 'Impossible de démarrer le quiz'
          });
        } else {
          console.log('Host start successful');
        }
      });
    },
    [ensureSocket, showToast]
  );

  /**
   * Host: Move to next question
   */
  const hostNext = useCallback(() => {
    socketRef.current?.emit('host.next', { sessionId }, (response) => {
      if (!response?.ok) {
        showToast({
          type: 'error',
          message: response?.error?.message || 'Erreur lors du passage à la question suivante'
        });
      }
    });
  }, [sessionId, showToast]);

  /**
   * Host: Reveal answers
   */
  const hostReveal = useCallback(() => {
    socketRef.current?.emit('host.reveal', { sessionId }, (response) => {
      if (!response?.ok) {
        showToast({
          type: 'error',
          message: response?.error?.message || 'Erreur lors de la révélation'
        });
      }
    });
  }, [sessionId, showToast]);

  /**
   * Host: End current question
   */
  const hostEndQuestion = useCallback(() => {
    socketRef.current?.emit('host.endQuestion', { sessionId }, (response) => {
      if (!response?.ok) {
        showToast({
          type: 'error',
          message: response?.error?.message || 'Erreur lors de la fin de la question'
        });
      }
    });
  }, [sessionId, showToast]);

  /**
   * Host: Skip current question
   */
  const hostSkip = useCallback(() => {
    socketRef.current?.emit('host.skip', { sessionId }, (response) => {
      if (!response?.ok) {
        showToast({
          type: 'error',
          message: response?.error?.message || 'Erreur lors du passage de la question'
        });
      }
    });
  }, [sessionId, showToast]);

  /**
   * Host: Kick participant
   */
  const hostKick = useCallback(
    (participantId) => {
      socketRef.current?.emit('host.kick', { sessionId, participantId }, (response) => {
        if (!response?.ok) {
          showToast({
            type: 'error',
            message: response?.error?.message || "Erreur lors de l'expulsion du participant"
          });
        }
      });
    },
    [sessionId, showToast]
  );

  /**
   * Host: Close session
   */
  const hostClose = useCallback(() => {
    return new Promise((resolve, reject) => {
      socketRef.current?.emit('host.close', { sessionId }, (response) => {
        if (response?.ok) {
          resolve();
        } else {
          const errorMessage =
            response?.error?.message || 'Erreur lors de la fermeture de la session';
          showToast({
            type: 'error',
            message: errorMessage
          });
          reject(new Error(errorMessage));
        }
      });
    });
  }, [sessionId, showToast]);

  const value = useMemo(
    () => ({
      socket: socketRef.current,
      connected,
      role,
      sessionId,
      code,
      status,
      players,
      playerCount,
      answeredCount,
      me,
      currentQuestion,
      lastReveal,
      questionResult,
      leaderboard,
      joinError,
      participantId,
      playerName,
      selection,
      lastAckStatus,
      finalResults,
      canExport: role === 'host' && status === 'podium',
      isHostControlsEnabled: connected && role === 'host' && status !== 'idle',
      // Player methods
      joinByCode,
      submitAnswer,
      clearSelection,
      leave,
      // Host methods
      hostJoinRoom,
      hostStart,
      hostNext,
      hostReveal,
      hostEndQuestion,
      hostSkip,
      hostKick,
      hostClose,
      // Legacy API compatibility
      hostSession: role === 'host' ? { sessionId, code } : null,
      playerSession: role === 'player' ? { sessionId, code } : null,
      setHostSession: (session) => {
        if (session) {
          setSessionId(session.sessionId);
          setCode(session.code);
          setRole('host');
        }
      },
      setPlayerSession: (session) => {
        if (session) {
          setSessionId(session.sessionId);
          setCode(session.code);
          setRole('player');
        }
      },
      clearHostSession: leave,
      clearPlayerSession: leave,
      isHostActive: () => role === 'host',
      isPlayerActive: () => role === 'player'
    }),
    [
      connected,
      role,
      sessionId,
      code,
      status,
      players,
      playerCount,
      answeredCount,
      me,
      currentQuestion,
      lastReveal,
      questionResult,
      leaderboard,
      joinError,
      participantId,
      playerName,
      selection,
      lastAckStatus,
      finalResults,
      joinByCode,
      submitAnswer,
      clearSelection,
      leave,
      hostJoinRoom,
      hostStart,
      hostNext,
      hostReveal,
      hostEndQuestion,
      hostSkip,
      hostKick,
      hostClose
    ]
  );

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      console.log('RoomProvider unmounting, cleaning up socket');
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  return <RoomContext.Provider value={value}>{children}</RoomContext.Provider>;
}

export function useRoom() {
  const context = useContext(RoomContext);
  if (!context) {
    throw new Error('useRoom must be used within RoomProvider');
  }
  return context;
}

/**
 * Get stored player info from localStorage
 */
export function getStoredPlayer() {
  const profile = JSON.parse(localStorage.getItem(KEY_PROFILE) || '{}');
  const lastCode = JSON.parse(localStorage.getItem(KEY_LAST_CODE) || '{}');
  return {
    name: profile.name || '',
    lastCode: lastCode.code || null
  };
}

/**
 * Get stored participantId for a given code
 */
export function getStoredParticipantId(code) {
  const map = JSON.parse(localStorage.getItem(KEY_PARTICIPANT_MAP) || '{}');
  return map[code] || null;
}
